import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import NotFound from 'modules/core/components/NotFound'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import FileViewer from 'modules/documents/components/FileViewer'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'

import AssignerHeader from './Header'
import MultiDocumentAssignerForm from './MultiDocumentAssignerForm'
import TitleHeader from './TitleHeader'

interface Props {
  documents: Document[]
  folders: Folder[]
  uploadProcess: UploadProcess
  onRefresh: () => void
}

const DocumentAssigner = ({ documents, folders, uploadProcess, onRefresh }: Props) => {
  const [index, setIndex] = useState(0)
  const [selectedIdsForEsignature, setSelectedIdsForEsignature] = useState<number[]>([])
  const [selectedDocument, setSelectedDocument] = useState<Document | undefined>()
  const [url, setUrl] = useState<string | undefined>()
  const requestEsignatureForm: AssignEsignatureForm = useLazyRef(
    () => new AssignEsignatureForm({ requestEsignature: false })
  ).current

  const onClickBack = () => {
    const nextDoc = index > 0 ? index - 1 : index
    setIndex(nextDoc)
  }

  const onClickForward = () => {
    const nextDoc = index < documents.length - 1 ? index + 1 : index
    setIndex(nextDoc)
  }

  const getRemoteDocumentUrl = async (): Promise<void> => {
    const absoluteUrl = selectedDocument?.fileUrl
    if (!absoluteUrl) return

    const api = ApiClient()
    try {
      const request = await api.get(absoluteUrl, {
        get_url: true,
        inline: true,
        source: 'web',
      })

      setUrl(request.url)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const updateUrl = async () => await getRemoteDocumentUrl()

  const selectDocumentByIndex = () => {
    let document = documents[index]

    if (!document) {
      const i = documents.length - 1
      document = documents[i]
      setIndex(i)
    }

    setSelectedDocument(document)
  }

  useEffect(() => {
    selectDocumentByIndex()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useUnsafeEffect(() => {
    selectDocumentByIndex()
  }, [index, documents]) // eslint-disable-line react-hooks/exhaustive-deps

  useUnsafeEffect(() => {
    updateUrl()
  }, [selectedDocument]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectIdsForEsignature = (selectedIds: number[]) => {
    setSelectedIdsForEsignature(selectedIds)
  }

  const documentFolder =
    folders.find((folder) => folder.id === selectedDocument?.folder?.id) ?? undefined

  const documentRootParentFolder =
    (documentFolder &&
      documentFolder.rootParentFolder?.id &&
      folders.find((folder) => folder.id === documentFolder.rootParentFolder?.id)) ||
    undefined

  if (!selectedDocument) return <NotFound />

  return (
    <ErrorBoundary>
      <Box
        background="grey200"
        borderRadius={{ all: 'abs006' }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        <AssignerHeader
          document={selectedDocument}
          documents={documents}
          index={index}
          onClickBack={onClickBack}
          onClickForward={onClickForward}
          uploadProcess={uploadProcess}
          onRefresh={onRefresh}
        />
        <Box flexDirection="row">
          <Box
            flexGrow
            flexDirection="column"
            overflowX="hidden"
            overflowY="hidden"
            minHeight="s600"
            border={{
              right: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <TitleHeader
              document={selectedDocument}
              folder={documentFolder}
              rootParentFolder={documentRootParentFolder}
              onDelete={onRefresh}
            />
            {url && selectedDocument.contentType && (
              <FileViewer
                isPending
                filename={selectedDocument.filename}
                mimeType={selectedDocument.contentType}
                url={url}
                isUrlAbsolute
              />
            )}
          </Box>
          <Box flexDirection="column" style={{ width: '450px' }}>
            <MultiDocumentAssignerForm
              document={selectedDocument}
              selectedIdsForEsignature={selectedIdsForEsignature}
              onSelectIdsForEsignature={onSelectIdsForEsignature}
              requestEsignatureForm={requestEsignatureForm}
              uploadProcess={uploadProcess}
            />
          </Box>
        </Box>
      </Box>
    </ErrorBoundary>
  )
}

export default observer(DocumentAssigner)
